import React from 'react';

/**
 * Badge Component
 * 
 * A reusable badge/chip component for displaying journey metadata and other categorical information.
 * Designed to maintain visual consistency across the platform with customizable styling.
 * 
 * @description This component provides a standardized way to display small pieces of information
 * with an icon and text. It's primarily used for journey metadata like duration, trip type,
 * transport preferences, and other categorical data throughout the application.
 * 
 * @usage
 * ```tsx
 * // Journey metadata (primary use case)
 * <Badge icon="⏱️">7 days</Badge>
 * <Badge icon="♻️">Round trip</Badge>
 * <Badge icon="🚆">Train preferred</Badge>
 * <Badge icon="✈️🚫">No flying</Badge>
 * 
 * // General categorical information
 * <Badge icon="🏷️">Premium</Badge>
 * <Badge icon="📍">Europe</Badge>
 * ```
 * 
 * @usedIn
 * - Journey.tsx: Displays journey metadata (duration, trip type, transport preference, flying preference)
 * - Potentially reusable across other parts of the platform for consistent chip styling
 * 
 * @designPrinciples
 * - Maintains consistent visual styling using CSS variables for theme compatibility
 * - Flexible icon support for visual context
 * - Responsive design with proper spacing and typography
 * - Reusable across different contexts while maintaining brand consistency
 */

interface BadgeProps {
  /** Icon to display before the text (emoji or icon string) */
  icon: string;
  /** Content to display in the badge */
  children: React.ReactNode;
  /** Additional CSS classes for custom styling */
  className?: string;
}

const Badge: React.FC<BadgeProps> = ({ icon, children, className = '' }) => {
  return (
    <span 
      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium border ${className}`}
      style={{
        backgroundColor: 'var(--surface-alt)',
        color: 'var(--text)',
        borderColor: 'var(--border)',
      }}
    >
      <span>{icon}</span>
      <span>{children}</span>
    </span>
  );
};

export default Badge; 